import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiPlay, FiCamera, FiGlobe, FiUsers } from 'react-icons/fi';
import HeroSection from '../components/HeroSection';
import ServiceCard from '../components/ServiceCard';
import ProjectCard from '../components/ProjectCard';
import ClientLogosCarousel from '../components/ClientLogosCarousel';
import CTASection from '../components/CTASection';
import SEO from '../components/SEO';
import { getFeaturedProjects } from '../utils/projectData';
import '../css/home.css';

const Home = () => {
  const homeStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Digital Shark Inc",
    "url": "https://digitalshark.tv",
    "description": "Caribbean Production & Broadcast Studio. We produce, broadcast, and stream high-impact content across the Caribbean. From Barbados to the Region — TV, Sports, Events & Pay-Per-View.",
    "publisher": {
      "@type": "Organization",
      "name": "Digital Shark Inc.",
      "url": "https://digitalshark.tv"
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://digitalshark.tv/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  const services = [
    {
      icon: <FiPlay className="w-8 h-8" />,
      title: 'Live Sports Production & Streaming',
      description: 'From local matches to regional tournaments, we deliver seamless live sports coverage with multicamera shoots, commentary, replays, and real-time streaming.',
      link: '/services'
    },
    {
      icon: <FiCamera className="w-8 h-8" />,
      title: 'Television & Series Production',
      description: 'We produce scripted series, talk shows, reality formats, lifestyle shows, and branded content from concept to final delivery.',
      link: '/services'
    },
    {
      icon: <FiGlobe className="w-8 h-8" />,
      title: 'Pay-Per-View Events & Broadcasts',
      description: 'Full PPV solutions for concerts, boxing matches, cultural festivals, and conferences with secure streaming platforms and monetization.',
      link: '/services'
    },
    {
      icon: <FiUsers className="w-8 h-8" />,
      title: 'Multi-platform Distribution',
      description: 'Distribution across TV stations, streaming services, regional syndication, and licensing to maximize your content reach.',
      link: '/services'
    }
  ];

  const featuredProjects = getFeaturedProjects(3);


  return (
    <div className="min-h-screen">
      <SEO 
        title="Digital Shark Inc - Caribbean Production & Broadcast Studio"
        description="Caribbean Production & Broadcast Studio. We produce, broadcast, and stream high-impact content across the Caribbean. From Barbados to the Region — TV, Sports, Events & Pay-Per-View."
        keywords="Caribbean production, broadcast studio, live streaming, television production, sports broadcasting, Pay Per View, PPV systems, Barbados, Caribbean media, content production, live events, streaming technology, French Village St Peter, Digital Shark Inc"
        url="/"
        structuredData={homeStructuredData}
      />
      {/* Hero Section */}
      <HeroSection />

      {/* Services Overview */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-800 mb-6">
              Key Services
            </h2>
            <p className="text-xl text-primary-600 max-w-3xl mx-auto">
              We produce, broadcast, and stream high-impact content across the Caribbean. Whether it's sports, television, documentaries, or live events, we handle the full production and distribution pipeline—on island, in region, and online.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <ServiceCard {...service} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Work */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-800 mb-6">
              Portfolio
            </h2>
            <p className="text-xl text-primary-600 max-w-2xl mx-auto">
              From concept development and filming through post production, broadcast operations, and monetization, we are your full-service media partner.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <ProjectCard {...project} />
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center mt-12"
          >
            <Link to="/projects" className="btn-secondary">
              View All Projects
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Where We Work */}
      <section className="section-padding bg-primary-800 text-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                About / Why Choose Us
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Based in Barbados with reach across the Caribbean, Digital Shark Inc. blends regional insight with international standards. From concept development and filming through post production, broadcast operations, and monetization (PPV, streaming, licensing), we are your full-service media partner.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-accent-400 mb-2">Why Work With Us</h3>
                  <ul className="text-gray-300 space-y-1">
                    <li>Deep regional knowledge + technical expertise</li>
                    <li>End-to-end solutions (you don't have to juggle vendors)</li>
                    <li>Scalable for small shows to stadium events</li>
                    <li>Robust technical infrastructure & redundancy</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-accent-400 mb-2">Regional Reach</h3>
                  <ul className="text-gray-300 space-y-1">
                    <li>Barbados (Headquarters)</li>
                    <li>Trinidad & Tobago</li>
                    <li>Jamaica</li>
                    <li>Guyana, St. Lucia, etc.</li>
                  </ul>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-accent-500 to-accent-600 rounded-2xl p-8">
                <h3 className="text-2xl font-bold mb-4">Let's create your next hit.</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span>Live Events Covered</span>
                    <span className="font-bold text-2xl">50+</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Countries Reached</span>
                    <span className="font-bold text-2xl">15+</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Platforms</span>
                    <span className="font-bold text-2xl">8+</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Languages</span>
                    <span className="font-bold text-2xl">3</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Client Logos Carousel */}
      <ClientLogosCarousel />

      {/* CTA Section */}
      <CTASection />
    </div>
  );
};

export default Home;
